import {patchState, signalStore, withHooks, withMethods, withProps, withState} from "@ngrx/signals";
import {addEntity, removeEntity, setAllEntities, setEntities, updateEntity, withEntities} from "@ngrx/signals/entities";

import {LamelareaImpl} from "./lamelarea.class";
import {LamelareaFactory} from "./lamelarea.factory";
import {LamellaSetFactory} from "./lamel/lamella-set.factory";
import {LamellaSet} from "./lamel/lamella-set.class";
import {Lamelarea} from "./lamelarea.interface";
import {clamp} from "three/src/math/MathUtils";
import {LamellaListingItem} from "../../types/lamella-listing-item.type";
import {inject} from "@angular/core";
import {SceneObjectManagerService} from "../../../threejs/service/scene-object.manager.service";
import {availableProductsStore} from "../../store/available-products.store";
import {syncCoverProfile, syncMountingProfile} from "./lamella-area.store-helper";
import {ColorConverter} from "../../../core/util/color-converter";
import {MountingProfile} from "./profiles/mounting-profile/mounting-profile";
import {v4 as uuid} from 'uuid';
import {ProjectRecord} from "../../types/project-record.type";
import {FrameAssemblyService} from "../../../assembly-configurator/service/frame-assembly.service";
import {materialStore} from "../../store/material.store";
import {serializeWithInfinity} from "../../util/infinity-serializer";
import {groupBy} from "../../util/groupBy";
import {LamellaAreaGroup} from "../../types/lamella-area-group.type";
import {Router} from "@angular/router";
import {environment} from "../../../../environments/environment.dev";
import {ProjectStore} from "../../store/project.store";
import {orderBy} from "../../util/orderBy";
import {RenderingService} from "../../../threejs/service/rendering.service";
import {FrameProfile} from "./profiles/frame-profile/frame-profile";
import {isDefined} from "../../../core/util/isDefined";
import {getFrameProfileType} from "./profiles/frame-profile/frame-profile.utils";

const initialGroupId = uuid();

export interface LamelareasState  {
    isLoading: boolean;
    hasInitialized: boolean;
    selectedLamellaAreaGroupId: string;
    lamellaAreaGroups?: { id: string, name: string }[];
    pristine: boolean;
}

const initialState: LamelareasState = {
    isLoading: false,
    hasInitialized: false,
    selectedLamellaAreaGroupId: initialGroupId,
    lamellaAreaGroups: [
        {id: initialGroupId, name: 'Ryhmä 1'}, // TODO: Set right group name with locale taken to consideration
    ],
    pristine: true,
}

const initHasStart = false;

export type LamellaAreaStore = InstanceType<typeof LamelareaStore>;

const demoSize = {
    horizontal: {
        width: 2000,
        height: 1500,
    },
    vertical: {
        width: 1500,
        height: 3400,
    },
}

const demoFramePosition = {
    horizontal: [
        { position: 250, reinforced: false },
        { position: 1750, reinforced: false },
    ],
    vertical: [
        { position: 200, reinforced: false },
        { position: 1700, reinforced: false },
        { position: 3200, reinforced: false },
    ],
}

export const LamelareaStore = signalStore(
    { providedIn: 'root'},
    withState<LamelareasState>(initialState),
    withEntities<LamelareaImpl>(),
    withProps(() => ({
        sceneObjectManagerService: inject(SceneObjectManagerService),
        availableProductStore: inject(availableProductsStore),
        materialStore: inject(materialStore),
    })),
    withMethods((
        store,
        material = inject(materialStore),
        frameAssemblyService = inject(FrameAssemblyService),
        router = inject(Router),
        projectStore = inject(ProjectStore),
        renderer = inject(RenderingService)
    ) => ({

        /* LAMELAREA */
        createLamelarea() {
            const order = store.entities().length + 1;
            const lamelAreaToCopy = store.entities().filter(lamellaArea=>lamellaArea.groupId === store.selectedLamellaAreaGroupId())[0];
            const lamelArea = LamelareaFactory.cloneLamelArea(lamelAreaToCopy);
            lamelArea.order = order;
            this._calculateAssemblyArrayForLamellaArea(lamelArea);
            patchState(store, addEntity(lamelArea));
            patchState(store, {pristine: false});
        },

        addLamelarea(lamelArea: LamelareaImpl) {
            patchState(store, addEntity(lamelArea));
            patchState(store, {pristine: false});
        },

        setLamelAreas(lamelAreas: LamelareaImpl[]) {
            console.log("test")
            patchState(store, setEntities(lamelAreas));
            patchState(store, {pristine: false});
        },

        removeLamelarea(id: string) {
            patchState(store, removeEntity(id));

            // Reset order after removing lamelarea
            const updatedEntities = store.entities();
            const reorderedEntities = updatedEntities.map((lamelarea, index) => {
                return {
                    ...lamelarea,
                    order: index + 1,
                };
            });
            patchState(store, setEntities(reorderedEntities));
            patchState(store, {pristine: false});
        },

        async updateLamelarea(id: string, changes: Partial<LamelareaImpl>): Promise<void> {

            if(id === undefined) return Promise.resolve();

            const originalValues = store.entities().find(lamelarea => lamelarea.id === id);
            if(!originalValues) return Promise.resolve();

            //update function for frame and mounting -profile
            const updateInstallation = async (framePositions: any, frameProfile?: FrameProfile, mountingProfile?: MountingProfile) => {
                //Tarkista että frame profile on olemassa
                if(frameProfile){
                    store.sceneObjectManagerService.reBuildFrameProfilesToLamellaArea(id, frameProfile, framePositions)
                }

                //fire and forget
                store.sceneObjectManagerService.rebuildMountingProfilesToLamellaArea(id, mountingProfile, framePositions)
            }

            if(changes.height || changes.width){
                changes.dimensionsPristine = false;
            }

            //update lamella
            if( changes.orientation && (originalValues.dimensionsPristine || originalValues.demoSize || changes.demoSize) ){
                const orientation = changes.orientation || originalValues.orientation;
                const demoWidth = orientation === 'horizontal' ? demoSize.horizontal.width : demoSize.vertical.width;
                const demoHeight = orientation === 'horizontal' ? demoSize.horizontal.height : demoSize.vertical.height;
                changes.width = demoWidth;
                changes.height = demoHeight;
            }

            if(
                changes.orientation
                || changes.width && (changes.orientation === 'horizontal' || originalValues.orientation === 'horizontal')
                || changes.height && (changes.orientation === 'vertical' || originalValues.orientation === 'vertical')
                || changes.automaticFrames
            ){
                const lamelAreaWithAssemblyValues = {...originalValues, ...changes};
                const assemblyValues = this._calculateAssemblyArrayForLamellaArea(lamelAreaWithAssemblyValues);
                changes = { ...changes, ...assemblyValues, automaticFrames: true };
            }

            patchState(store, updateEntity({id: id, changes: changes}));
            patchState(store, {pristine: false});

            if(changes.orientation){
                //Do not run any other updates before this if there is a change in orientation
                await store.sceneObjectManagerService.updateLamellaAreaOrientation(id, changes.orientation)

                //changes.demoSize = originalValues.demoSize
                const lamellaArea = store.entities().find(lamelarea => lamelarea.id === id);

                await store.sceneObjectManagerService.updateLamellaAreaSize(id, lamellaArea.width, lamellaArea.height)
                updateInstallation(lamellaArea.framePositions, lamellaArea.frameProfile, lamellaArea.mountingProfile);
            }

            if('frameProfile' in changes){
                if(!changes.frameProfile){
                    store.sceneObjectManagerService.removeFrameProfileFromLamellaArea(id)
                }
                else {
                    const positions = changes.framePositions ?? originalValues.framePositions;
                    const demoFramePositions = originalValues.orientation === 'horizontal' ? demoFramePosition.horizontal : demoFramePosition.vertical;
                    const framePositions = originalValues.demoSize ? demoFramePositions : positions;

                    //fire and forget
                    store.sceneObjectManagerService.reBuildFrameProfilesToLamellaArea(id, changes.frameProfile, framePositions)

                }
            }

            if(changes.mountingProfile){
                originalValues.mountingProfile.materialType !== changes.mountingProfile.materialType
                    ? store.sceneObjectManagerService.updateMountingAndFrameProfileMaterialAndColor(id, changes.mountingProfile)
                    : store.sceneObjectManagerService.updateMountingAndFrameProfileColor(id, changes.mountingProfile.color, changes.mountingProfile);
            }

            //orientation change possibly changes frame positions and it also calculates frame positions; deny duplicate update
            if(changes.framePositions && !changes.orientation){

                //Apply if user sets frameProfiles on and DemoSize is set
                const demoFramePositions = originalValues.orientation === 'horizontal' ? demoFramePosition.horizontal : demoFramePosition.vertical;
                const framePositions = originalValues.demoSize ? demoFramePositions : changes.framePositions;
                updateInstallation(framePositions, originalValues.frameProfile, originalValues.mountingProfile);

            }

            if ((changes.width || changes.height) && !originalValues.demoSize ) {

                const lamellaArea = store.entities().find(lamelarea => lamelarea.id === id);
                const width = changes.width || lamellaArea.width;
                const height = changes.height || lamellaArea.height;

                await store.sceneObjectManagerService.updateLamellaAreaSize(id, width, height)
                updateInstallation(lamellaArea.framePositions, lamellaArea.frameProfile, lamellaArea.mountingProfile);

            }

            if ('demoSize' in changes) {

                const orientation = changes.orientation || originalValues.orientation;
                const demoWidth = orientation === 'horizontal' ? demoSize.horizontal.width : demoSize.vertical.width;
                const demoHeight = orientation === 'horizontal' ? demoSize.horizontal.height : demoSize.vertical.height;
                const demoFramePositions = orientation === 'horizontal' ? demoFramePosition.horizontal : demoFramePosition.vertical;

                const isDemo = changes.demoSize;
                const width = isDemo ? demoWidth : originalValues.width;
                const height = isDemo ? demoHeight : originalValues.height;
                const framePositions = isDemo ? demoFramePositions: originalValues.framePositions;

                // It's important to run updateLamellaAreaSize before rebuildMountingProfilesToLamellaArea and reBuildFrameProfilesToLamellaArea
                await store.sceneObjectManagerService.updateLamellaAreaSize(id, width, height)

                updateInstallation(framePositions, originalValues.frameProfile, originalValues.mountingProfile);
            }
        },

        getLamelarea(id: string) {
            return store.entities().find(lamelarea => lamelarea.id === id);
        },

        getLamelareas(groupId: string = store.selectedLamellaAreaGroupId()) {
            return store.entities().filter(lamelarea => lamelarea.groupId === groupId);
        },

        getAllLamelareas() {
            return store.entities();
        },

        /* LAMELAREA GROUP */
        setSelectLamelareaGroup(id: string) {
            if(id === store.selectedLamellaAreaGroupId()) return;
            patchState(store, {selectedLamellaAreaGroupId: id});
        },

        getLamelareaGroup(id: string) {
            return store.lamellaAreaGroups().find(lamellaAreaGroup => lamellaAreaGroup.id === id);
        },

        setPristine() {
            patchState(store, { pristine: true });
        },

        updateLamelSet(lamelSetId: string, changes: Partial<LamellaSet>) {
            console.log("test", changes)
            // 1. Etsitään lamelArea (eli lamelli-alue), josta lamellaSet löytyy
            const lamelArea = store.entities().find(
                lamelarea => lamelarea.lamellaSets.find(lamellaSet => lamellaSet.id === lamelSetId)
            );

            // Jos lamelArea ei löytynyt, ei tehdä mitään
            if (!lamelArea) {
                console.warn(`LamellaSet with id ${lamelSetId} not found in any lamelArea.`);
                return;
            }

            // 2. Etsitään vanha lamellaSet
            const oldLamellaSet = lamelArea.lamellaSets.find(lamellaSet => lamellaSet.id === lamelSetId);
            if (!oldLamellaSet) {
                console.warn(`LamellaSet with id ${lamelSetId} not found.`);
                return;
            }

            //Jos materiaali tyyppiä muutetaan, asetetaan oletus väri.
            if(changes.material){
                if(changes.material === "Powder Coated"){
                    changes.color = ColorConverter.convert('#3f888f');
                }
                else {//Anodized
                    const color = ColorConverter.getAllColors("Anodized")[0].HEX;
                    changes.color = ColorConverter.convert(color, "Anodized");
                }
            }

            const newLamellaSet = LamellaSetFactory.createLamellaSetFromExisting(oldLamellaSet, changes);

            /*const oldSerialized = JSON.stringify(oldLamellaSet);
            const newSerialized = JSON.stringify(newLamellaSet);

            // Jos mikään tieto ei oliekasti ole muuttunut niin ei päivitetä
            if (oldSerialized === newSerialized) {
                //return;
            }*/

            // Jos päästiin tähän asti, meillä on oikeita muutoksia => päivitetään
            const updatedLamellaSets = lamelArea.lamellaSets.map(lamellaSet => {
                if (lamellaSet.id === lamelSetId) {
                    // Luodaan lopullinen päivitettävä lamellaSet
                    return newLamellaSet;
                }
                return lamellaSet;
            });

            // Koostetaan lamelarea-muutokset
            const lamelAreaChanges: Partial<LamelareaImpl> = { lamellaSets: updatedLamellaSets };

            let updateMountingProfile3d = false;

            // Tarkistetaan, onko kyseessä listan ensimmäinen lamelArea
            //if (lamelArea.id === store.entities()[0].id) {
                if (lamelArea.syncMountingProfile) {
                    const mountingProfile = syncMountingProfile(lamelArea, changes);
                    if (mountingProfile) {
                        lamelAreaChanges.mountingProfile = mountingProfile as MountingProfile;
                        updateMountingProfile3d = true;
                    }
                }

                if (lamelArea.syncCoverProfile) {
                    const coverProfile = syncCoverProfile(lamelArea, changes);
                    if (coverProfile) {
                        lamelAreaChanges.coverProfile = coverProfile;
                    }
                }

                // Päivitetään store
                patchState(
                    store,
                    updateEntity({
                        id: lamelArea.id,
                        changes: lamelAreaChanges
                    })
                );
                patchState(store, {pristine: false});
            //}

            /** Start ThreeJS object updates **/

            //Color and Material

            if(changes.color && changes.material ) {
                store.sceneObjectManagerService.updateLamellaSetMaterialAndColor(lamelArea.id, lamelSetId, changes.material, changes.color);
                if(updateMountingProfile3d) {
                    const mountingProfile = store.entities().find(lamellaArea => lamellaArea.id === lamelArea.id)?.mountingProfile;
                    store.sceneObjectManagerService.updateMountingAndFrameProfileMaterialAndColor(lamelArea.id, mountingProfile);
                }
            }
            else if (changes.color) {
                store.sceneObjectManagerService.updateLamellaSetColor(lamelArea.id, lamelSetId, changes.color, updatedLamellaSets );
                if(updateMountingProfile3d) {
                    const mountingProfile = store.entities().find(lamellaArea => lamellaArea.id === lamelArea.id)?.mountingProfile;
                    store.sceneObjectManagerService.updateMountingAndFrameProfileColor(lamelArea.id, changes.color, mountingProfile);
                }
            }

            // Jos jokin seuraavista on muuttunut, uudelleenrakennetaan lamellaSetit
            if (
                changes.lamellaRepeat ||
                changes.object3dUrl ||
                changes.lamellaSpace ||
                changes.lamellaSpaceBetween
            ) {
                store.sceneObjectManagerService.rebuildLamellaSets(lamelArea.id, updatedLamellaSets);
            }
        },

        updateLamellaSetProduct(lamellaSetId: string, product: LamellaListingItem ) {

            const entity: Lamelarea = store.entities().find(lamellaArea => lamellaArea.lamellaSets.find(lamellaSet => lamellaSet.id === lamellaSetId));

            const lamellaSetIndex: number = entity.lamellaSets.findIndex(lamellaSet => lamellaSet.id === lamellaSetId);
            let lamellaSet: LamellaSet = entity.lamellaSets.find(lamellaSet => lamellaSet.id === lamellaSetId);

            lamellaSet.lamellaId = product.id;
            lamellaSet.lamellaSpace = clamp(lamellaSet.lamellaSpace, product.minSpaceTop, product.maxSpace);
            lamellaSet.spaceAfter = clamp(lamellaSet.spaceAfter, product.minSpaceBottom, product.maxSpace);

            const updatedLamellaSets = [...entity.lamellaSets];
            updatedLamellaSets.splice(lamellaSetIndex, 1, lamellaSet);

            patchState(store, updateEntity({
                id: entity.id,
                changes: { lamellaSets: updatedLamellaSets }
            }));
            patchState(store, {pristine: false});
        },

        setLamellaAreaLamelInstallationType(lamelAreaId: string, type: number) {

            const lamelArea = store.entities().find(lamelarea => lamelarea.id === lamelAreaId);
            if (!lamelArea) {
                console.warn(`LamelArea with id ${lamelAreaId} not found.`);
                return;
            }

            let updatedLamellaSets: any[] = [];

            //Yhden säleen malli
            if(type == 1) {
                const updatedLamellaSet: LamellaSet = LamellaSetFactory.createLamellaSetFromExisting(lamelArea.lamellaSets[0]);
                updatedLamellaSet.lamellaRepeat = Infinity;

                updatedLamellaSets = [updatedLamellaSet];
                patchState(store, updateEntity({
                    id: lamelArea.id,
                    changes: {
                        lamellaSets: updatedLamellaSets,
                        lamellaSetType: type
                    }
                }));

            }

            //Kahden säleen malli
            if(type == 2) {

                const updatedLamellaSet: LamellaSet = LamellaSetFactory.createLamellaSetFromExisting(lamelArea.lamellaSets[0]);
                updatedLamellaSet.lamellaRepeat = 2;

                updatedLamellaSets = [updatedLamellaSet, LamellaSetFactory.cloneLamellaSet(updatedLamellaSet)];

                patchState(store, updateEntity({
                    id: lamelArea.id,
                    changes: {
                        lamellaSets: updatedLamellaSets,
                        lamellaSetType: type
                    }
                }));
            }

            patchState(store, {pristine: false});

            //do not wait
            store.sceneObjectManagerService.rebuildLamellaSets(lamelArea.id, updatedLamellaSets);
        },

        getLamellaAreaGroups(): Array<LamellaAreaGroup> {
            return groupBy(store.entities(), 'groupId').map((group: Array<Lamelarea>) => ({
                    id: store.lamellaAreaGroups().find(lamellaAreaGroup => lamellaAreaGroup.id === group[0].groupId)?.id,
                    name: store.lamellaAreaGroups().find(lamellaAreaGroup => lamellaAreaGroup.id === group[0].groupId)?.name,
                    lamellaAreas: this.handleLamellaAreasForSave(group)
            }));
        },

        //any typing due to typing missmatch between ui and backend
        handleLamellaAreasForSave(lamellaAreas: Lamelarea[]): any[] {
            return  lamellaAreas.map(lamellaArea => ({
                ...lamellaArea,

                coverProfile:lamellaArea?.coverProfile?.id,
                coverProfileProductCode: lamellaArea?.coverProfile?.productCode,
                coverProfileColor: lamellaArea?.coverProfile?.color?.HEX,
                coverProfileMaterial: material.getId(lamellaArea?.coverProfile?.materialType),

                frameProfile:lamellaArea?.frameProfile?.id,
                frameProfileProductCode: lamellaArea?.frameProfile?.productCode,
                frameProfileColor: lamellaArea?.frameProfile?.color?.HEX,
                frameProfileMaterial: material.getId(lamellaArea?.frameProfile?.materialType),

                mountingProfile:lamellaArea?.mountingProfile?.id,
                mountingProfileProductCode: lamellaArea?.mountingProfile?.productCode,
                mountingProfileColor: lamellaArea?.mountingProfile?.color?.HEX,
                mountingProfileMaterial: material.getId(lamellaArea?.mountingProfile?.materialType),

                lamellaSets: lamellaArea.lamellaSets.map( (lamellaSet: any) => ({
                        ...lamellaSet,
                        color: lamellaSet.color.HEX,
                        lamellaRepeat: serializeWithInfinity(lamellaSet.lamellaRepeat),
                        material: material.getId(lamellaSet.material)
                }))
            }))
        },

        loadFromProjectRecord(projectRecord: ProjectRecord) {
            const lamellaAreaGroups = projectRecord.lamellaAreaGroups.map( group => ({id: group.id as string, name: group.name}));
            const lamellaAreas = projectRecord.lamellaAreaGroups.flatMap( group =>

                (group.lamellaAreas as LamelareaImpl[]).map(i => {

                    const maxFrameSpan = Math.min(...i.lamellaSets.map((lamellaSet: LamellaSet) => lamellaSet.maxFrameSpan));
                    const span = i.orientation === 'horizontal' ? i.width : i.height;
                    const assemblyAutoConfig = frameAssemblyService.watchNeededFramesLamellaArea(span, maxFrameSpan);

                    return {
                        ...i,

                        groupId: group.id,

                        mountingProfile: isDefined(i?.mountingProfile)
                            ?   {
                                    ...i.mountingProfile,
                                    color: i?.mountingProfile?.color && ColorConverter.convert(i?.mountingProfile?.color as any, i.mountingProfile.materialType === "Anodized" ? "Anodized" : "RAL"),
                                    object3dUrl: i.mountingProfile.productCode.toLowerCase() + '.obj'
                                }
                            : null,

                        coverProfile: isDefined(i?.coverProfile)
                            ?   {
                                    ...i.coverProfile,
                                    color: i?.coverProfile?.color && ColorConverter.convert(i?.coverProfile?.color as any, i.coverProfile.materialType === "Anodized" ? "Anodized" : "RAL"),
                                    object3dUrl: i.coverProfile.productCode+'.obj'
                                }
                            : null,

                        frameProfile: isDefined(i?.frameProfile)
                            ?  {
                                    ...i.frameProfile,
                                    color: i?.mountingProfile?.color && ColorConverter.convert(i?.mountingProfile?.color as any, i.mountingProfile.materialType === "Anodized" ? "Anodized" : "RAL"),
                                    object3dUrl: i?.frameProfile?.productCode.toLowerCase() + '.obj',
                                    type: getFrameProfileType(i?.frameProfile.productCode)
                                }
                            : null,

                        assemblyAutoConfig: assemblyAutoConfig,

                        assemblyArray: frameAssemblyService.provideAssemblyArrayLamellaAreaFromFramePositions(i.id, span, orderBy(i.framePositions, "position"), assemblyAutoConfig),

                        lamellaSets: i.lamellaSets.map(set => ({
                                ...set,
                                color: ColorConverter.convert(set.color as any, set.material === "Anodized" ? "Anodized" : "RAL")
                            })
                        ).map(LamellaSetFactory.cloneLamellaSet)

                    } as LamelareaImpl

                })

            )

            store.sceneObjectManagerService.removeAllObjects();
            patchState(store, setAllEntities(lamellaAreas.map(LamelareaFactory.cloneLamelArea)));
            patchState(store, {lamellaAreaGroups, hasInitialized:true, selectedLamellaAreaGroupId: lamellaAreaGroups[0].id});
        },

        async addLamellaAreaGroup(name: string) {
            const groupId = uuid();
            const groups = [...store.lamellaAreaGroups(), {id: groupId, name}];
            patchState(store, {lamellaAreaGroups: groups});
            return this.setInitialProduct(true, groupId);
        },

        removeLamellaAreaGroup(id: string) {
            const groups = store.lamellaAreaGroups().filter(group => group.id !== id);
            patchState(store, {lamellaAreaGroups: groups});
            const lamellaAreas = store.entities().filter(lamellaArea => lamellaArea.groupId === id);
            lamellaAreas.forEach(lamellaArea => this.removeLamelarea(lamellaArea.id));
        },

        renameLamellaAreaGroup(id: string, name: string) {
            const groups: any[] = store.lamellaAreaGroups().map(group => ({...group, name: group.id === id ? name : group.name}));
            patchState(store, {lamellaAreaGroups: groups});
        },

        async splitFrameSpan(lamellaAreaId: string, index: number) {
            const lamellaArea = store.entities().find(lamellaArea => lamellaArea.id === lamellaAreaId);
            const assemblyArray = frameAssemblyService.splitSpanLamellaArea(index, lamellaArea.assemblyArray, lamellaArea.orientation === 'horizontal'? lamellaArea.width : lamellaArea.height);
            const framePositions = frameAssemblyService.provideFramePositionsLamellaArea(assemblyArray);
            return this.updateLamelarea(lamellaAreaId, {assemblyArray, framePositions, automaticFrames: false});
        },

        async conjoinFrameSpans(lamellaAreaId: string, index: number) {
            const lamellaArea = store.entities().find(lamellaArea => lamellaArea.id === lamellaAreaId);
            const assemblyArray = frameAssemblyService.conjoinSpansLamellaArea(index, lamellaArea.assemblyArray);
            const framePositions = frameAssemblyService.provideFramePositionsLamellaArea(assemblyArray);
            return this.updateLamelarea(lamellaAreaId, {assemblyArray, framePositions, automaticFrames: false});
        },

        async setSpanValue(lamellaAreaId: string, index: number, value: number) {
            const lamellaArea = store.entities().find(lamellaArea => lamellaArea.id === lamellaAreaId);
            const assemblyArray = frameAssemblyService.calculateFrameSpanValuesOnInputLamellaArea(index, value, lamellaArea.assemblyArray);
            const framePositions = frameAssemblyService.provideFramePositionsLamellaArea(assemblyArray);
            return this.updateLamelarea(lamellaAreaId, {assemblyArray, framePositions, automaticFrames: false});
        },

        async setOverflowSpanValue(lamellaAreaId: string, index: number, value: number) {
            const lamellaArea = store.entities().find(lamellaArea => lamellaArea.id === lamellaAreaId);
            const assemblyArray = frameAssemblyService.overflowSpanChangeLamellaArea(index, value, lamellaArea.orientation === 'horizontal' ? lamellaArea.width : lamellaArea.height , lamellaArea.assemblyArray);
            const framePositions = frameAssemblyService.provideFramePositionsLamellaArea(assemblyArray);
            return this.updateLamelarea(lamellaAreaId, {assemblyArray, framePositions, automaticFrames: false});
        },

        _calculateAssemblyArrayForLamellaArea(lamellaArea: LamelareaImpl) {

            const maxFrameSpan = Math.min(...lamellaArea.lamellaSets.map((lamellaSet: LamellaSet) => lamellaSet.maxFrameSpan));
            const span = lamellaArea.orientation === 'horizontal' ? lamellaArea.width : lamellaArea.height;
            lamellaArea.assemblyAutoConfig = frameAssemblyService.watchNeededFramesLamellaArea(span, maxFrameSpan);
            lamellaArea.assemblyArray = frameAssemblyService.provideAssemblyArrayLamellaArea(uuid(), span, lamellaArea.assemblyAutoConfig);
            lamellaArea.framePositions = frameAssemblyService.provideFramePositionsLamellaArea(lamellaArea.assemblyArray);

            return {
                assemblyAutoConfig: lamellaArea.assemblyAutoConfig,
                assemblyArray: lamellaArea.assemblyArray,
                framePositions: lamellaArea.framePositions
            };
        },

        async fetchProjectIfQueryParams() {
            const params = new URLSearchParams(window.location.search);
            const project = params.get("project");
            const projectRecord = params.get("projectRecord");
            if (!(project && projectRecord)) { return false; }
            const isSupported = await projectStore.isSupported(project, projectRecord);
            if (!isSupported) { return false; }
            const projectObj = await projectStore.fetchProject(project, projectRecord);
            return router.navigate([], { queryParams: {} } ).then(() => {
                projectObj?.projectRecords?.[0] && this.loadFromProjectRecord(projectObj.projectRecords[0])
            });
        },

        async setInitialProduct(overrideInitialization: boolean = false, groupId: string = initialGroupId) {
            if( (store.hasInitialized() || store.entities().length > 0) && !overrideInitialization ) { return false; }
            const params = new URLSearchParams(window.location.search);
            const productCode = params.get("preSelectedProduct") || environment.DEFAULT_PRODUCT_CODE;
            let selectedProduct = await store.availableProductStore.provideProduct(productCode);
            if(!selectedProduct) { selectedProduct = await store.availableProductStore.provideProduct(environment.DEFAULT_PRODUCT_CODE); } //fallback on missing product to fetch default product
            const lamellaArea = LamelareaFactory.createLamelAreaFromLamellaListingItem(selectedProduct, undefined, groupId);
            this._calculateAssemblyArrayForLamellaArea(lamellaArea);
            this.addLamelarea(lamellaArea);
            patchState( store, {hasInitialized: true} );
            return true;
        }

    })),

    //sessionSave<any>('lamellaAreaStore', ['selectedLamelareaGroupId','lamellaAreaGroups','entities'], 'setLamelAreas')
);